/* Lazy cursor streamer for metaobject reference lists over the Storefront API.
   window.MetaobjectStream.streamReferences(config) returns a pull-based async iterator over
   pages of reference nodes; each pull resolves to { ok: true, fieldKey, nodes } or { ok: false, error }.
   Fields fieldKey, fieldKey_2, ... are walked in order until one is missing. */
(() => {
  if (window.MetaobjectStream) return;

  const buildQuery = (nodeFragment) => `query MetaobjectReferences($handle: MetaobjectHandleInput!, $fieldKey: String!, $first: Int!, $after: String) {
    metaobject(handle: $handle) {
      field(key: $fieldKey) {
        references(first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          nodes { ${nodeFragment} }
        }
      }
    }
  }`;

  const fetchPage = async ({ token, apiVersion, metaobject, nodeFragment }, fieldKey, after, first) => {
    const response = await fetch(`/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: buildQuery(nodeFragment),
        variables: { handle: metaobject, fieldKey, first, after },
      }),
    });
    if (!response.ok) throw new Error(`Storefront request failed with ${response.status}`);

    const payload = await response.json();
    if (Array.isArray(payload?.errors) && payload.errors.length) {
      const message = payload.errors
        .map((error) => error?.message)
        .filter(Boolean)
        .join('; ');
      throw new Error(message || 'Storefront GraphQL request failed');
    }

    const metaobjectResult = payload?.data?.metaobject;
    if (!metaobjectResult) throw new Error('Storefront metaobject was not found');

    const field = metaobjectResult.field;
    if (!field) return undefined;

    const connection = field.references;
    if (!connection) throw new Error('Storefront metaobject references were unavailable');

    return {
      nodes: (connection.nodes || []).filter(Boolean),
      hasNextPage: Boolean(connection.pageInfo?.hasNextPage),
      endCursor: connection.pageInfo?.endCursor,
    };
  };

  const streamReferences = (config) => {
    const { fieldKey = 'rows', pageSize = 12, apiVersion = '2025-07' } = config;
    const resolved = { ...config, apiVersion };
    const keyAt = (index) => (index === 0 ? fieldKey : `${fieldKey}_${index + 1}`);

    /* Cursor state lives only in this closure; consumers see a sequence. */
    let fieldIndex = 0;
    let cursor;
    let fieldExhausted = false;

    const next = async () => {
      if (fieldExhausted) {
        fieldIndex += 1;
        cursor = undefined;
        fieldExhausted = false;
      }

      let page;
      try {
        page = await fetchPage(resolved, keyAt(fieldIndex), cursor, pageSize);
      } catch (error) {
        return { done: false, value: { ok: false, error } };
      }

      /* A missing or unset field ends the sequence. */
      if (!page) return { done: true, value: undefined };

      cursor = page.endCursor;
      fieldExhausted = !page.hasNextPage;
      return { done: false, value: { ok: true, fieldKey: keyAt(fieldIndex), nodes: page.nodes } };
    };

    const iterator = { next };
    iterator[Symbol.asyncIterator] = () => iterator;
    return iterator;
  };

  const buildBatchQuery = (type, fieldKey, handles, nodeFragment, first) =>
    `query {\n${handles
      .map(
        (handle, index) =>
          `m${index}: metaobject(handle: {type: ${JSON.stringify(type)}, handle: ${JSON.stringify(handle)}}) { field(key: ${JSON.stringify(fieldKey)}) { references(first: ${first}) { pageInfo { hasNextPage } nodes { ${nodeFragment} } } } }`
      )
      .join('\n')}\n}`;

  const fetchReferencesBatch = async (config) => {
    const { token, apiVersion = '2025-07', type, fieldKey, handles, nodeFragment, first = 250, chunkSize = 30 } = config;
    const byHandle = new Map();
    const overflow = [];

    const chunks = [];
    for (let start = 0; start < handles.length; start += chunkSize) {
      chunks.push(handles.slice(start, start + chunkSize));
    }

    await Promise.all(
      chunks.map(async (chunk) => {
        let payload;
        try {
          const response = await fetch(`/api/${apiVersion}/graphql.json`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
            body: JSON.stringify({ query: buildBatchQuery(type, fieldKey, chunk, nodeFragment, first) }),
          });
          if (!response.ok) throw new Error(`Storefront batch request failed with ${response.status}`);
          payload = await response.json();
        } catch {
          chunk.forEach((handle) => byHandle.has(handle) || byHandle.set(handle, []));
          return;
        }

        chunk.forEach((handle, index) => {
          const connection = payload?.data?.[`m${index}`]?.field?.references;
          byHandle.set(handle, (connection?.nodes ?? []).filter(Boolean));
          if (connection?.pageInfo?.hasNextPage) overflow.push(handle);
        });
      })
    );

    for (const handle of overflow) {
      const iterator = streamReferences({ token, apiVersion, metaobject: { type, handle }, fieldKey, pageSize: 250, nodeFragment });
      const nodes = [];
      for (;;) {
        let step;
        try {
          step = await iterator.next();
        } catch {
          break;
        }
        if (step.done) break;
        if (!step.value?.ok) break;
        step.value.nodes.forEach((node) => nodes.push(node));
      }
      if (nodes.length) byHandle.set(handle, nodes);
    }

    return byHandle;
  };

  window.MetaobjectStream = Object.freeze({ streamReferences, fetchReferencesBatch });
})();
